import { ProDetailList, ProText } from '@bedrock/eed-react';
import {
  FormCard,
  FormDescriptions,
  FormItemAddressDetail,
  FormItemAdministrativeArea,
  FormItemBranchInstitutionAddress,
  FormItemBranchInstitutionArea,
  FormItemBranchInstitutionsDetailList,
  FormItemBusinessAddress,
  FormItemBusinessArea,
  FormItemBusinessScope,
  FormItemCreditCode,
  FormItemEnglishName,
  FormItemEstablishTime,
  FormItemFormerName,
  FormItemInvoiceType,
  FormItemPaidUpCapital,
  FormItemPersonEmail,
  FormItemRegisterCapital,
  FormItemRegisterCountry,
  FormItemTelPhone,
  FormItemVendorAliasName,
  FormItemVendorName,
  FormItemVendorType,
  FormItemTaxType,
  FormItemInsuredNumberWithFile,
  FormItemLicenseRangeDateWithFile,
} from '@purchase/supplier';
import { Button } from '@bedrock/components';
import { ChevronDown, ChevronUp } from '@bedrock/icons-react';
import { EllipsisTooltip } from '@mpc/components';
import classNames from 'classnames';
import { FC, useState } from 'react';
import React from 'react';
import { ProTextWithCopy } from '../../components/ProTextWithCopy';
import { ProLink } from '../../components/ProLink';
import { ProRadioBoolean } from '../../components/ProRadioBoolean';
import RelatedMaterials from './RelatedMaterials';
import './index.less';

const EnterpriseNoFixUnitPrice: FC<{
  isExternal?: boolean;
  isOverseas?: boolean;
  isCompany?: boolean;
  formDetail?: any;
}> = ({
  isExternal = false,
  isOverseas = false,
  isCompany = false,
  formDetail,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <FormCard title="基本信息" className="mpc-supplier-new-detail-basic-info">
      <div
        className={classNames({
          'mpc-basic-info-collapsed': !expanded,
        })}
      >
        <FormDescriptions
          columns={expanded ? 2 : 3}
          bordered={expanded ? undefined : false}
          labelStyle={expanded ? { width: 160 } : undefined}
          collapsed={!expanded}
        >
          {/* 展开态独有：供应商名称（排在最前） */}
          {expanded && (
            <FormItemVendorName
              label="供应商名称"
              name={['vendorBasicInfo', 'enterpriseName']}
            />
          )}

          {!isOverseas && (
            <ProTextWithCopy
              label="统一社会信用代码"
              name={['vendorBasicInfo', 'creditCode']}
            />
          )}

          {/* 展开态独有：供应商类型、注册国家/地区 */}
          {expanded && (
            <FormItemVendorType
              label="供应商类型"
              name={['vendorBasicInfo', 'vendorType']}
            />
          )}
          {expanded && (
            <FormItemRegisterCountry
              label="注册国家/地区"
              name={['vendorBasicInfo', 'country']}
            />
          )}

          {!isExternal && (
            <ProTextWithCopy
              label="VMDM供应商编码"
              name={['vendorBasicInfo', 'vendorId']}
            />
          )}
          {!isExternal && (
            <ProTextWithCopy
              label="MPC供应商编码"
              name={['vendorBasicInfo', 'externalVendorId']}
            />
          )}

          {!isOverseas && (
            <ProText
              label="企业类型"
              name={['vendorBasicInfo', 'companyType']}
              render={(value) => <EllipsisTooltip text={value ?? '-'} />}
            />
          )}
          {!isOverseas && (
            <FormItemFormerName
              label="曾用名"
              name={['vendorBasicInfo', 'formerName']}
            />
          )}

          <FormItemVendorAliasName
            label="简称"
            name={['vendorBasicInfo', 'enterpriseAliasName']}
          />
          <FormItemEnglishName
            label="英文全称"
            name={['vendorBasicInfo', 'englishName']}
          />

          {!isOverseas && (
            <FormItemRegisterCapital
              label="注册资本（万）"
              name={['vendorBasicInfo', 'registerCapital']}
            />
          )}
          {!isOverseas && (
            <FormItemPaidUpCapital
              label="实缴资本（万）"
              name={['vendorBasicInfo', 'paidUpCapital']}
            />
          )}

          <FormItemEstablishTime
            label="成立日期"
            name={['vendorBasicInfo', 'establishTime']}
          />

          {/* 展开态独有：税种、参保人数 */}
          {expanded && isOverseas && (
            <FormItemTaxType
              label="税种"
              name={['vendorBasicInfo', 'taxType']}
            />
          )}
          {expanded && !isOverseas && (
            <FormItemInsuredNumberWithFile
              label="参保人数"
              name={['vendorBasicInfo', 'insuredNumberWithFile']}
            />
          )}

          {/* 展开态独有：发票类型、开票税点 */}
          {expanded && (
            <FormItemInvoiceType
              label="发票类型"
              name={['vendorBasicInfo', 'invoiceType']}
            />
          )}
          {expanded && !isOverseas && (
            <ProText
              label="开票税点"
              name={['vendorBasicInfo', 'extendAttributeInfo', 'invoicingTax']}
            />
          )}

          <FormItemLicenseRangeDateWithFile
            label={isCompany ? '营业期限' : '证件有效期'}
            isCompany={isCompany}
            isOverseas={isOverseas}
            name={['vendorBasicInfo', 'licenseRangeDateWithFile']}
          />

          {/* 展开态独有：所属地区、注册地址 */}
          {expanded && (
            <FormItemAdministrativeArea
              label="所属地区"
              name={['vendorBasicInfo', 'administrativeArea']}
            />
          )}
          {expanded && (
            <FormItemAddressDetail
              label="注册地址"
              name={['vendorBasicInfo', 'address']}
            />
          )}

          <FormItemBusinessArea
            label="主要经营地区"
            name={['vendorBasicInfo', 'businessArea']}
          />
          <FormItemBusinessAddress
            label="主要经营地址"
            name={['vendorBasicInfo', 'businessAddress']}
          />

          {!isExternal && !isOverseas && (
            <FormItemPersonEmail
              label="邮箱"
              name={['vendorBasicInfo', 'email']}
            />
          )}
          {!isExternal && !isOverseas && (
            <FormItemTelPhone
              label="电话"
              name={['vendorBasicInfo', 'telPhone']}
            />
          )}

          {!isOverseas && (
            <ProLink
              label="主页网址"
              name={['vendorBasicInfo', 'homeSite']}
              fieldProps={{}}
            />
          )}

          {/* 展开态独有：KOL、单一来源、经营范围、其他经营地 */}
          {expanded && !isExternal && (
            <ProRadioBoolean
              label="是否为KOL合作MCN"
              name={['vendorBasicInfo', 'extendAttributeInfo', 'isKOLForMCN']}
            />
          )}
          {expanded && (
            <ProRadioBoolean
              label="是否单一来源"
              name={[
                'vendorBasicInfo',
                'extendAttributeInfo',
                'isSingleSource',
              ]}
            />
          )}

          {expanded && (
            <FormItemBusinessScope
              label="经营范围"
              name={['vendorBasicInfo', 'businessScope']}
              span={2}
            />
          )}

          {expanded && (
            <FormItemBranchInstitutionsDetailList
              label="其他经营地"
              name={['vendorBasicInfo', 'branchInstitutions']}
              span={2}
            >
              <ProDetailList.Item
                label="#"
                name="#"
                meta={{ columnProps: { width: 50 } }}
              >
                {(value, record, index) => {
                  return index + 1;
                }}
              </ProDetailList.Item>
              <FormItemBranchInstitutionArea
                label="其他经营地区"
                name="manageArea"
                width={180}
                meta={{
                  columnProps: { width: 160, className: 'font-semibold' },
                }}
              />
              <FormItemBranchInstitutionAddress
                label="其他经营地址"
                name="manageAddress"
                width={180}
              />
            </FormItemBranchInstitutionsDetailList>
          )}
        </FormDescriptions>
      </div>

      {/* 展开/收起切换按钮 */}
      <div className="mt-2 text-xs text-color-2">
        <Button
          type="link"
          onClick={() => setExpanded(!expanded)}
          suffixIcon={expanded ? <ChevronUp /> : <ChevronDown />}
        >
          {expanded ? '收起' : '展开全部'}
        </Button>
      </div>

      {/* 相关材料 */}
      <RelatedMaterials isExternal={isExternal} formDetail={formDetail} />
    </FormCard>
  );
};

export default EnterpriseNoFixUnitPrice;
